// @mui material components
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import { shadeColor } from "utils/colors";

export default styled(Badge)(({ theme, ownerState }) => {
  const { palette, typography, borders, functions } = theme;
  const { color, circular, border, size, indicator, variant, container, children, maxWidth } = ownerState;

  const { white, dark, gradients, badgeColors } = palette;
  const { size: fontSize, fontWeightBold } = typography;
  const { borderRadius, borderWidth } = borders;
  const { pxToRem, linearGradient } = functions;

  // padding values
  const paddings = {
    xs: "0.575em 0.775em 0.4em",
    sm: "0.55em 0.9em",
    md: "0.75em 1em 0.65em",
    lg: "0.925em 1.375em 0.85em",
  };

  // fontSize value
  const fontSizeValue = size === "xs" ? fontSize.xxs : fontSize.xs;

  // border value
  const borderValue = border ? `${borderWidth[3]} solid ${white.main}` : "none";

  // borderRadius value
  let borderRadiusValue = size === "xs" ? borderRadius.sm : borderRadius.md;

  if (circular) {
    borderRadiusValue = borderRadius.section;
  }

  // styles for the badge with indicator={true}
  const indicatorStyles = (sizeProp) => {
    let widthValue = pxToRem(20);
    let heightValue = pxToRem(20);

    if (sizeProp === "medium") {
      widthValue = pxToRem(24);
      heightValue = pxToRem(24);
    } else if (sizeProp === "large") {
      widthValue = pxToRem(32);
      heightValue = pxToRem(32);
    }

    return {
      width: "100%",
      height: heightValue,
      display: "grid",
      placeItems: "center",
      textAlign: "center",
      borderRadius: "50%",
      padding: 0,
      border: borderValue,
    };
  };

  // styles for the badge with variant="gradient"
  const gradientStyles = (colorProp) => {
    const backgroundValue = gradients[colorProp]
      ? linearGradient(gradients[colorProp].main, gradients[colorProp].state)
      : linearGradient(gradients.info.main, gradients.info.state);
    const colorValue = colorProp === "light" ? dark.main : white.main;

    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  // styles for the badge with variant="contained"
  const containedStyles = (colorProp) => {
    const backgroundValue = badgeColors[colorProp]
      ? badgeColors[colorProp].background
      : badgeColors.info.background;
    let colorValue = badgeColors[colorProp] ? badgeColors[colorProp].text : badgeColors.info.text;

    if (colorProp === "light") {
      colorValue = dark.main;
    }
    return {
      background: backgroundValue,
      color: colorValue,
    };
  };

  const colorStyles = (colorProp) => {
    if (colorProp[0] === "#") {
      const r = parseInt(colorProp.substr(1, 2), 16);
      const g = parseInt(colorProp.substr(3, 2), 16);
      const b = parseInt(colorProp.substr(5, 2), 16);

      // Calcula el brillo relativo del color de fondo
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      return {
        background: colorProp,
        color: brightness > 200 ? dark.main : white.main,
      };
    } else {
      return {
        background: dark.main,
        color: white.main,
      };
    }
  };

  const colorGradientStyles = (colorProp) => {
    if (colorProp[0] === "#") {
      const secondColor = shadeColor(colorProp, 30); // Puedes ajustar el valor -20 para obtener el tono deseado
      const gradient = `linear-gradient(310deg, ${colorProp}, ${secondColor})`;

      const r = parseInt(colorProp.substr(1, 2), 16);
      const g = parseInt(colorProp.substr(3, 2), 16);
      const b = parseInt(colorProp.substr(5, 2), 16);

      // Calcula el brillo relativo del color de fondo
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      return {
        background: gradient,
        color: brightness > 200 ? dark.main : white.main,
      };
    } else {
      return {
        background: linearGradient(gradients.dark.main, gradients.dark.state),
        color: white.main,
      };
    }
  };

  // styles for the badge with no children and container={false}
  const standAloneStyles = () => ({
    position: "static",
    transform: "none",
    fontSize: pxToRem(9),
  });

  // styles for the badge with container={true}
  const containerStyles = () => ({
    position: "relative",
    transform: "none",
  });

  return {
    "& .MuiBadge-badge": {
      height: "auto",
      width: "100%",
      padding: paddings[size] || paddings.xs,
      fontSize: fontSizeValue,
      fontWeight: fontWeightBold,
      textTransform: "uppercase",
      lineHeight: 1,
      textAlign: "center",
      whiteSpace: "nowrap",
      verticalAlign: "baseline",
      border: borderValue,
      borderRadius: borderRadiusValue,
      width: maxWidth ? variant === "colorGradient" ? "180px" : "120px" : "auto",
      maxWidth: maxWidth ? variant === "colorGradient" ? "12vw" : "8vw" : "auto",
      ...(indicator && indicatorStyles(size)),
      ...(variant === "gradient" && gradientStyles(color)),
      ...(variant === "contained" && containedStyles(color)),
      ...(variant === "color" && colorStyles(color)),
      ...(variant === "colorGradient" && colorGradientStyles(color)),
      ...(!children && !container && standAloneStyles(color)),
      ...(container && containerStyles(color)),
    },
  };
});
